/**
 * F13 - the admin master views show EVERY stored field (Ashok ruling,
 * 2026-08-25, built 2026-09-08).
 *
 * THE DEFECT THIS CLOSES, and why it kept recurring. Each master table's
 * column list was a hardcoded literal. Every time the backend contract
 * gained a column (the five location columns of 2026-08-13, Requirement
 * Type on 2026-08-14, the two vocabularies on 2026-09-08) the column had
 * to be added here by hand, and mostly was not: the category view showed
 * 11 of 16 columns, so an admin who had just uploaded a taxonomy could not
 * see whether Requirement Type, Paired Code, Synonyms, Schedule Role or
 * Location Notes had landed. "An admin reading the screen concludes the
 * data is what they see."
 *
 * THE FIX IS DERIVATION, NOT A LONGER LITERAL. The columns are the CURATED
 * definitions (which carry a label and a renderer - the Flag badge, the
 * Scope Elements expander) PLUS every other key the returned rows actually
 * carry, in first-seen order, rendered generically. A column added on the
 * server therefore appears here with no frontend change. Two keys are
 * excluded, and only two: `id` (an opaque UUID nothing authored - it
 * appears in no file and identifies nothing to a human) and `is_active`
 * (rendered as the Inactive badge on the first column and the Activate
 * control, not as a column). That exclusion list is the ONLY place a
 * stored field can be hidden, and it is anchored by
 * MasterDataManagement.everyStoredField.test.jsx against the shared
 * fixture backend/tests/fixtures/master_admin_visible_fields.json.
 *
 * MASKING IS STILL THE SERVER'S. A non-admin never receives the admin-only
 * keys (routes/master_data strips them), so derivation shows a non-admin
 * exactly what the server sent and nothing more - the same principle as
 * columnIfDataPresent, now applied to every key rather than a named few.
 */

// Keys that are never rendered as a column. See the module comment for
// why each one is here; anything not in this list IS shown.
export const NEVER_AS_A_COLUMN = Object.freeze(['id', 'is_active']);

export const EMPTY_CELL = '—';

/** snake_case -> "Title Case", with the initialisms the masters use. */
export const humanizeKey = (key) =>
  String(key)
    .split('_')
    .filter(Boolean)
    .map((w) => (w === 'unspsc' ? 'UNSPSC' : w === 'uom' ? 'UoM' : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(' ');

const ISO_DATE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

/**
 * A stored value as display text. Populated values are ALWAYS non-empty
 * text (a populated column rendered blank is the F13 defect in another
 * form); genuine emptiness - null, undefined, "", [] - is the em-dash.
 */
export const storedValueText = (val) => {
  if (val === null || val === undefined) return EMPTY_CELL;
  if (Array.isArray(val)) return val.length ? val.map(storedValueText).join(', ') : EMPTY_CELL;
  if (typeof val === 'boolean') return val ? 'Yes' : 'No';
  if (typeof val === 'object') return JSON.stringify(val);
  const text = String(val);
  if (text.trim() === '') return EMPTY_CELL;
  if (ISO_DATE.test(text)) return text.slice(0, 10);
  return text;
};

/**
 * Curated columns first, then every other key present on ANY row, in
 * first-seen order, minus NEVER_AS_A_COLUMN. `curated` entries keep their
 * own label and renderer; a curated key absent from every row still
 * renders (an empty master shows its headers), which is why curated
 * definitions remain the place to put a column that must exist.
 */
export const deriveMasterColumns = (rows, curated) => {
  const seen = new Set(curated.map((c) => c.key));
  const extras = [];
  for (const row of rows || []) {
    for (const key of Object.keys(row || {})) {
      if (seen.has(key) || NEVER_AS_A_COLUMN.includes(key)) continue;
      seen.add(key);
      extras.push({
        key,
        label: humanizeKey(key),
        derived: true,
        render: (val) => storedValueText(val),
      });
    }
  }
  return [...curated, ...extras];
};
